import type { Uuid } from "@/domains/global/identifiers";
import type { UploadableAttachment, SkippedAttachment } from "@/modules/file-import/upload/types";

/**
 * Entry point that initiated the one-click import (for analytics)
 */
export type OneClickImportEntryPoint = "onboarding" | "profile_menu";

/**
 * Source application for the one-click import (for analytics)
 */
export enum OneClickImportSource {
  AppleNotes = "apple-notes",
  Obsidian = "obsidian",
  Other = "other",
}

/**
 * Selection state for a section of notes
 */
export enum SectionSelectionState {
  All = "all",
  None = "none",
  Indeterminate = "indeterminate",
}

export type ImportableNoteAttachment = UploadableAttachment;

/**
 * Represents a note available for import
 */
export interface ImportableNote {
  id: Uuid;
  title: string;
  markdown: string;
  /**
   * Source folder path (e.g., from Apple Notes nested folders).
   * Example: ["Work", "Mem", "Auto Organize"] represents Work -> Mem -> Auto Organize.
   * The last folder is used for section grouping, all folders become collections.
   */
  folders: string[];
  isSelected: boolean;
  isLocked: boolean;
  lockedReason?: string;
  isPinned: boolean;
  createdAt?: string;
  updatedAt?: string;
  subtitle?: string;
  tags: string[];
  /** Original Apple Note ID (used for on-demand attachment loading) */
  appleNoteId?: number;
  /** Attachments (images, files) ready for upload. Undefined means not loaded yet. */
  attachments?: ImportableNoteAttachment[];
  /** Attachments that were skipped (e.g., due to size limits) */
  skippedAttachments?: SkippedAttachment[];
}

/**
 * Import status for tracking the one-click import process
 */
export enum ImportStatus {
  Idle = "idle",
  InProgress = "in_progress",
  Success = "success",
  Failed = "failed",
}

/**
 * Loading status for fetching notes from Apple Notes
 */
export enum LoadingStatus {
  Loading = "loading",
  Done = "done",
  Error = "error",
}
